import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import './UnavailabilityBox.scss';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { Block, TableRestaurant } from '@mui/icons-material';
import { AccountData, OrderPageData, UnavailabilityData, UnavailabilityDetailData } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { useAuth } from '../../session/InternalAuthProvider';
import UnavailabilityBoxPopper from './UnavailabilityBoxPopper';
import { MAX_PARALLEL_PERSON_CAPACITY } from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/PrivateUnavailabilityDrawer';
import { WorkshopCircle } from '../../common/ui/WorkshopCircle';
import { getOrderPages } from '../../utils/AccountUtils';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import {
  SchedulerGridDisplayMode,
  SchedulerResourceGrid,
} from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/SchedulerResourceGrid';

const UnavailabilityBox = (props: { unavailability: UnavailabilityData; viewType?: 'month' | 'day'; css?: any }) => {
  const { unavailability, viewType, css } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { calendarView } = useCalendar();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const { t } = useTranslation();
  const language = useLanguage();

  const { authState } = useAuth();

  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);

  const orderPagesWithArchived = getOrderPages(account, true);
  const openUnavailability = (event: any) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const getTimeText = () => {
    if (unavailability.all_day) {
      return 'All day';
    }
    return `${moment.unix(unavailability.start_time).tz('utc').format(timeFormat)} - ${moment
      .unix(unavailability.end_time)
      .tz('utc')
      .format(timeFormat)}`;
  };

  // Month cells are tight: one line (time + description); the full block details stay in the popper.
  // Phones keep the full box, the one line gets cropped there.
  const isMonth = viewType === 'month';
  if (isMonth && !isPhone) {
    const orderPageLabel = unavailability.order_page_id
      ? orderPagesWithArchived.find((o: OrderPageData) => o.id === unavailability.order_page_id)?.label[language]
      : undefined;
    const description = [unavailability.description || t('calendar.unavailability'), orderPageLabel]
      .filter(Boolean)
      .join(' - ');
    const timeText = unavailability.all_day ? t('calendar.unavailability_dialog.all_day') : getTimeText();
    return (
      <>
        <Grid
          onClick={openUnavailability}
          className="unavailability-box month-unavailability-box"
          title={`${timeText} · ${description}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 22, minHeight: 22, paddingInline: 0.75 }}>
          <Block sx={{ fontSize: 12, flexShrink: 0 }} />
          <Typography
            component="span"
            fontSize="12px"
            fontWeight={600}
            dir={unavailability.all_day ? undefined : 'ltr'}
            sx={{ flexShrink: 0 }}>
            {timeText}
          </Typography>
          <Typography component="span" fontSize="12px" noWrap sx={{ minWidth: 0 }}>
            {description}
          </Typography>
        </Grid>
        <UnavailabilityBoxPopper anchorEl={anchorEl} setAnchorEl={setAnchorEl} unavailability={unavailability} />
      </>
    );
  }

  let dynamicStyles = null;
  if (css) {
    dynamicStyles = {
      width: `calc(${css.width}% - 6px)`,
      insetInlineStart: `calc(${css.left}% + 3px)`,
      top: `${css.top - 1}px`,
      height: `${css.height - 5}px`,
      position: 'absolute',
    };
  }

  return (
    <>
      <Grid
        onClick={openUnavailability}
        className={`unavailability-box flex-1${isMonth ? ' month-unavailability-box' : ''}`}
        sx={dynamicStyles}>
        <Grid className="text-center unavailability-box-inner">
          {isMonth && (
            <p dir="ltr">
              <strong>{getTimeText()}</strong>
            </p>
          )}
          <p>
            <span>{unavailability.description ? unavailability.description : t('calendar.unavailability')}</span>
            {unavailability.order_page_id && (
              <span>
                - {orderPagesWithArchived.filter((o: any) => o.id === unavailability.order_page_id)[0].label[language]}
              </span>
            )}
          </p>
          {unavailability.max_parallel_workshops != null && <p>{unavailability.max_parallel_workshops} 🔢</p>}
          {unavailability.custom_details && (
            <>
              {Object.keys(unavailability.custom_details).map((key) => {
                return (
                  <p dir="ltr">
                    <span>{unavailability.custom_details?.[key]}</span>
                    &nbsp;
                    <span>{key === MAX_PARALLEL_PERSON_CAPACITY ? t('availabilitySettings.blockResources') : key}</span>
                  </p>
                );
              })}
            </>
          )}
          {!isEmpty(unavailability.unavailability_details) &&
            unavailability.unavailability_details!.length < orderPagesWithArchived.length && (
              <Grid container justifyContent="center" gap="2px" my="4px">
                {unavailability.unavailability_details
                  ?.filter((x: UnavailabilityDetailData) => {
                    // Filter out order pages that have shared resources
                    const orderPage = orderPagesWithArchived.find((o: OrderPageData) => o.id === x.order_page_id);
                    return !orderPage?.shared_resources_allocations?.customer_choosable_resources?.length;
                  })
                  .map((x: UnavailabilityDetailData) => {
                    const customDetails = orderPagesWithArchived.find(
                      (o: OrderPageData) => o.id === x.order_page_id,
                    )?.custom_details;
                    const keyName = customDetails?.[0]?.name;
                    return (
                      <WorkshopCircle
                        withTooltip
                        account={account}
                        orderPageId={
                          orderPagesWithArchived.find((orderPage: OrderPageData) => orderPage.id === x.order_page_id)
                            ?.id
                        }
                        language={language}
                        customDetailAmoount={
                          x.custom_details && keyName && x.custom_details?.[keyName] > 1
                            ? x.custom_details?.[keyName]
                            : undefined
                        }
                      />
                    );
                  })}
              </Grid>
            )}
          {(unavailability.shared_resources_blocks?.automatic_dividable_resources?.length || 0) > 0 && (
            <Grid container justifyContent="center" gap={1} my="4px">
              {unavailability.shared_resources_blocks?.automatic_dividable_resources?.map((block) => {
                const resource = account.shared_resources?.automatic_dividable_resources?.find(
                  (r) => r.id === block.resource_id,
                );
                if (!resource) return null;

                return (
                  <p dir="ltr" key={block.resource_id}>
                    <span>{resource.label[language]}: </span>
                    <span>{block.blocked_capacity}</span>
                  </p>
                );
              })}
            </Grid>
          )}
          {(unavailability.shared_resources_blocks?.customer_choosable_resources?.length || 0) > 0 && (
            <Grid container justifyContent="center" alignItems="center" gap={0.5} my="2px">
              {unavailability.shared_resources_blocks?.customer_choosable_resources?.map((block) => {
                const resource = account.shared_resources?.customer_choosable_resources?.find(
                  (r) => r.id === block.resource_id,
                );
                if (!resource || block.blocked_capacity === 0) return null;

                // Find workshops that use this customer choosable resource
                const affectedWorkshops = orderPagesWithArchived.filter((orderPage: OrderPageData) =>
                  orderPage.shared_resources_allocations?.customer_choosable_resources?.some(
                    (allocation) => allocation.resource_id === block.resource_id,
                  ),
                );

                return (
                  <Grid
                    container
                    alignItems="center"
                    gap={0.5}
                    key={block.resource_id}
                    justifyContent="center"
                    flexWrap="nowrap">
                    {affectedWorkshops.length > 0 ? (
                      affectedWorkshops.map((orderPage: OrderPageData) => (
                        <WorkshopCircle
                          key={orderPage.id}
                          withTooltip
                          account={account}
                          orderPageId={orderPage.id}
                          language={language}
                        />
                      ))
                    ) : (
                      <TableRestaurant style={{ fontSize: '14px' }} />
                    )}
                    <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{block.blocked_capacity}</span>
                  </Grid>
                );
              })}
            </Grid>
          )}
          {(unavailability.shared_resources_blocks?.scheduler_configs?.length || 0) > 0 &&
            calendarView !== CalendarView.DAY && (
              <Grid container justifyContent="center" gap={1} my="4px" flexDirection="column">
                {unavailability.shared_resources_blocks?.scheduler_configs?.map((block) => {
                  const config = account.shared_resources?.scheduler_configs?.find(
                    (c) => c.id === block.scheduler_config_id,
                  );
                  if (!config) {
                    return null;
                  }

                  const blockedPositions = block.blocked_mask
                    .split('')
                    .map((bit, index) => (bit === '1' ? index : -1))
                    .filter((pos) => pos !== -1);
                  if (blockedPositions.length === 0) {
                    return null;
                  }
                  return (
                    <SchedulerResourceGrid
                      key={block.scheduler_config_id}
                      schedulerConfig={config}
                      blockedMask={block.blocked_mask}
                      readOnly
                      displayMode={SchedulerGridDisplayMode.INLINE}
                    />
                  );
                })}
              </Grid>
            )}
        </Grid>
      </Grid>
      <UnavailabilityBoxPopper anchorEl={anchorEl} setAnchorEl={setAnchorEl} unavailability={unavailability} />
    </>
  );
};

export default UnavailabilityBox;
